import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { formatUTCDateDateTimeWidget } from '~/services/TimeService';

import type { TagDraftSchedule } from '../TagForm';
import { useCreateTagSchedule, useUpdateTagSchedule } from '../hooks';

import { type TagScheduleFormData, validateTagScheduleFormData } from './TagScheduleForm';
import TagSchedulesList from './TagSchedulesList';

interface Props {
  tagId?: string;
  schedules: Array<TagDraftSchedule>;
  persistOnSave?: boolean;
  onSchedulesChange: (schedules: Array<TagDraftSchedule>) => void;
}

export default function TagSchedules({ tagId, schedules, persistOnSave = false, onSchedulesChange }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [scheduleToEdit, setScheduleToEdit] = useState<TagDraftSchedule | null>(null);
  const shouldPersistImmediately = !!tagId && !persistOnSave;
  const createTagSchedule = useCreateTagSchedule();
  const updateTagSchedule = useUpdateTagSchedule();

  const handleSaveSchedule = (schedule: TagDraftSchedule) => {
    const uniqueSchedules = Array.from(new Map([...schedules, schedule].map((item) => [item.localId, item])).values());
    onSchedulesChange(uniqueSchedules);
    if (persistOnSave || !tagId) {
      const alreadyExists = schedules.some((existingSchedule) => existingSchedule.localId === schedule.localId);
      enqueueSnackbar(`Schedule ${alreadyExists ? 'updated' : 'created'}. It will be saved when you save the tag.`, {
        variant: 'success',
      });
    }
    setScheduleToEdit(null);
  };

  const handleStartEdit = (schedule: TagDraftSchedule) => {
    setScheduleToEdit(schedule);
  };

  const handleCancelEdit = () => {
    setScheduleToEdit(null);
  };

  const extractValidatedScheduleData = (formData: TagScheduleFormData) => {
    const validationError = validateTagScheduleFormData(formData);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'error' });
      return null;
    }

    const { activeStartDate, activeEndDate, brands, regions, travelStartDate, travelEndDate } = formData;

    if (!activeStartDate || !activeEndDate) {
      return null;
    }

    return {
      brands,
      regions,
      activeStartDate,
      activeEndDate,
      travelStartDate: travelStartDate || undefined,
      travelEndDate: travelEndDate || undefined,
    };
  };

  const handleSaveCreate = async (formData: TagScheduleFormData) => {
    const data = extractValidatedScheduleData(formData);
    if (!data) {
      return;
    }

    if (shouldPersistImmediately) {
      await createTagSchedule.mutateAsync({ tagId, ...data });
      return true;
    }

    handleSaveSchedule({
      localId: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      tagId,
      ...data,
    });
    return true;
  };

  const handleSaveEdit = async (formData: TagScheduleFormData) => {
    if (!scheduleToEdit) {
      return;
    }

    const data = extractValidatedScheduleData(formData);
    if (!data) {
      return;
    }

    if (shouldPersistImmediately && scheduleToEdit.id) {
      await updateTagSchedule.mutateAsync({
        id: scheduleToEdit.id,
        data,
      });
      handleCancelEdit();
      return true;
    }

    handleSaveSchedule({ ...scheduleToEdit, ...data });
    return true;
  };

  const handleEndSchedule = async (schedule: TagDraftSchedule) => {
    const nowUTC = formatUTCDateDateTimeWidget(new Date());

    if (shouldPersistImmediately && schedule.id) {
      await updateTagSchedule.mutateAsync({
        id: schedule.id,
        data: { activeEndDate: nowUTC },
      });
      return;
    }

    handleSaveSchedule({
      ...schedule,
      activeEndDate: nowUTC,
    });
  };

  return (
    <TagSchedulesList
      tagId={tagId}
      schedules={schedules}
      persistOnSave={persistOnSave}
      onEditClick={handleStartEdit}
      editingSchedule={scheduleToEdit}
      onCancelEdit={handleCancelEdit}
      onSaveCreate={handleSaveCreate}
      onSaveEdit={handleSaveEdit}
      onEndSchedule={handleEndSchedule}
      isSavingInline={createTagSchedule.isPending || updateTagSchedule.isPending}
    />
  );
}
